import React, { useState, useEffect } from 'react';
import { Pillar } from '../types';
import { pillarsAPI } from '../services/api';
import './CreatePillarModal.css';

interface CreatePillarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPillarCreated: (pillar: Pillar) => void;
}

const CreatePillarModal: React.FC<CreatePillarModalProps> = ({
  isOpen,
  onClose,
  onPillarCreated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    article: '',
    pillarParent: ''
  });
  const [parentPillars, setParentPillars] = useState<Pillar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Helper function to format pillar display text with level and L1 pillar info
  const formatPillarDisplayText = (pillar: Pillar): string => {
    let displayText = pillar.name;
    
    // Add level information if available
    if (pillar.levelFromL1) {
      displayText = `L${pillar.levelFromL1} - ${displayText}`;
    }
    
    // Add L1 pillar information if available
    if (pillar.L1pillar) {
      displayText += ` ( L1: ${pillar.L1pillar} )`;
    }
    
    return displayText;
  };

  // Fetch parent pillars when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchParentPillars = async () => {
        try {
          const pillars = await pillarsAPI.getAll();
          // Sort pillars by level (levelFromL1) in ascending order
          const sortedPillars = pillars.sort((a, b) => {
            const levelA = a.levelFromL1 || 0;
            const levelB = b.levelFromL1 || 0;
            return levelA - levelB;
          });
          setParentPillars(sortedPillars);
        } catch (error) {
          console.error('Failed to fetch parent pillars:', error);
        }
      };
      fetchParentPillars();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setProgress(0);
      setProgressMessage('Preparing to create pillar...');

      // Step 1: Validate form data
      setProgress(15);
      setProgressMessage('Validating form data...');
      await new Promise(resolve => setTimeout(resolve, 200));

      // Step 2: Prepare data
      setProgress(30);
      setProgressMessage('Preparing pillar data...');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 3: Create on server
      setProgress(60);
      setProgressMessage('Creating pillar on server...');
      const newPillar = await pillarsAPI.create({
        name: formData.name.trim(),
        article: formData.article.trim(),
        ...(formData.pillarParent ? { pillarParent: formData.pillarParent } : {})
      });

      // Step 4: Finalize
      setProgress(90);
      setProgressMessage('Finalizing creation...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setProgress(100);
      setProgressMessage('Pillar created successfully!');
      await new Promise(resolve => setTimeout(resolve, 500));

      onPillarCreated(newPillar);
      
      // Reset form
      setFormData({
        name: '',
        article: '',
        pillarParent: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to create pillar:', error);
      setError('Failed to create pillar. Please try again.');
      setProgressMessage('Failed to create pillar');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      article: '',
      pillarParent: ''
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create New Pillar</h2>
          <button className="modal-close" onClick={handleCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {loading && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progress-message">{progressMessage}</div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter pillar name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="article">Description</label>
            <textarea
              id="article"
              name="article"
              value={formData.article}
              onChange={handleInputChange}
              placeholder="Enter pillar description"
              rows={4}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="pillarParent">Parent Pillar</label>
            <select
              id="pillarParent"
              name="pillarParent"
              value={formData.pillarParent}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">Select a parent pillar...</option>
              {parentPillars.map((pillar) => (
                <option key={pillar._id} value={pillar._id}>
                  {formatPillarDisplayText(pillar)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--gray"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--green"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Pillar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePillarModal; 